import { ApiRequestError } from './client-http';
export type ResourceFetcher = <T>(url: string, options: { signal?: AbortSignal }) => Promise<T>;
const errorMessage = (error: unknown) => error instanceof Error ? error.message : 'Could not load this resource.';

const MAX_ENTRIES = 32;
type Entry<T> = { data?: T; error?: string; loading: boolean; updatedAt: number };
type Snapshot<T> = { identityKey: string; generation: number; entries: ReadonlyMap<string, Entry<T>>; lastData?: T; accessKey?: string };

/** A mounted resource owns its cache. No account data is put in browser storage
 * or shared with another viewer, profile instance, or server render. */
export function createClientResourceCache<T>(identityKey: string, staleTime: number, accessKey: ((data: T) => string) | undefined, fetcher: ResourceFetcher) {
  const initial: Snapshot<T> = { identityKey, generation: 0, entries: new Map() };
  let snapshot = initial, disposed = false;
  const listeners = new Set<() => void>();
  const requests = new Map<string, { controller: AbortController; promise: Promise<void> }>();
  const publish = (next: Snapshot<T>) => { snapshot = next; listeners.forEach(listener => listener()); };
  const abort = (url: string) => { requests.get(url)?.controller.abort(); requests.delete(url); };
  const abortAll = () => { requests.forEach(request => request.controller.abort()); requests.clear(); };
  function put(url: string, entry: Entry<T>, replaceAliases = false) {
    const entries = new Map(snapshot.entries), previous = entries.get(url);
    // A new authoritative response can change a default profile page. Retire
    // aliases to its previous response rather than relabeling old tab data.
    if (replaceAliases && previous?.data !== undefined) {
      for (const [key, value] of entries) if (key !== url && value.data === previous.data) entries.delete(key);
    }
    entries.delete(url); entries.set(url, entry);
    while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value!);
    publish({ ...snapshot, entries, lastData: entry.data ?? snapshot.lastData });
  }
  function load(url: string, force = false): Promise<void> {
    if (disposed) return Promise.resolve();
    const previous = snapshot.entries.get(url);
    if (!force && previous?.data !== undefined && !previous.error && Date.now() - previous.updatedAt < staleTime) return Promise.resolve();
    const pending = requests.get(url);
    if (pending && !force) return pending.promise;
    if (pending) abort(url);
    const controller = new AbortController();
    put(url, { ...previous, error: undefined, loading: true, updatedAt: previous?.updatedAt ?? 0 });
    const promise = fetcher<T>(url, { signal: controller.signal }).then(data => {
      if (controller.signal.aborted || disposed) return;
      const nextAccess = accessKey?.(data);
      if (snapshot.accessKey !== undefined && nextAccess !== snapshot.accessKey) {
        // A successful response can revoke access too (private/blocked profile,
        // changed follow access or activity visibility). Cancel older reads.
        abortAll();
        publish({ identityKey, generation: snapshot.generation, entries: new Map(), accessKey: nextAccess });
      } else if (nextAccess !== snapshot.accessKey) {
        snapshot = { ...snapshot, accessKey: nextAccess };
      }
      put(url, { data, loading: false, updatedAt: Date.now() }, true);
    }).catch(error => {
      if (controller.signal.aborted || disposed) return;
      if (error instanceof ApiRequestError && [401, 403, 404].includes(error.status)) {
        // Do not retain previously accessible private content after a denial.
        abortAll();
        publish({ identityKey, generation: snapshot.generation, entries: new Map([[url, { error: errorMessage(error), loading: false, updatedAt: 0 }]]) });
      } else {
        put(url, { data: snapshot.entries.get(url)?.data, error: errorMessage(error), loading: false, updatedAt: previous?.updatedAt ?? 0 });
      }
    }).finally(() => { if (requests.get(url)?.controller === controller) requests.delete(url); });
    requests.set(url, { controller, promise });
    return promise;
  }
  return {
    subscribe: (listener: () => void) => { disposed = false; listeners.add(listener); return () => { listeners.delete(listener); }; },
    getSnapshot: () => snapshot,
    getServerSnapshot: () => initial,
    load,
    invalidate() { abortAll(); publish({ ...initial, generation: snapshot.generation + 1 }); },
    reload(url: string) {
      if (disposed) return;
      abortAll();
      const current = snapshot.entries.get(url);
      publish({ ...snapshot, entries: current ? new Map([[url, current]]) : new Map(), lastData: current?.data ?? snapshot.lastData });
      void load(url, true);
    },
    update(url: string, data: T, expectedData: T | undefined, { invalidateOthers = false }: { invalidateOthers?: boolean } = {}) {
      if (disposed) return;
      const previous = snapshot.entries.get(url), entries = new Map(snapshot.entries);
      if (!previous || previous.data !== expectedData) return;
      if (invalidateOthers) abortAll(); else abort(url);
      for (const [key, value] of entries) {
        if (key === url || previous?.data !== undefined && value.data === previous.data) {
          abort(key); entries.set(key, { data, loading: false, updatedAt: Date.now() });
        } else if (invalidateOthers) entries.delete(key);
      }
      entries.set(url, { data, loading: false, updatedAt: Date.now() });
      publish({ ...snapshot, entries, lastData: data });
    },
    remember(url: string, alias: string) {
      const entry = snapshot.entries.get(url);
      if (!disposed && alias !== url && entry?.data !== undefined && !entry.error && !entry.loading) put(alias, entry);
    },
    dispose() { disposed = true; abortAll(); publish(initial); },
  };
}

export type ClientResourceCache<T> = ReturnType<typeof createClientResourceCache<T>>;
